import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import { createApiClient, apiClient } from '../../../api/apiClient';
import {
  SOURCE_OPTIONS,
  TYPE_OPTIONS,
  PRIORITY_OPTIONS,
  transformUserToAssigneeOption,
  emailToDisplayName,
  formatAuthUserName,
} from '../../ActionPlan/utils/formOptions';

/**
 * Custom hook for Site Evaluation Action Plan form
 * Extends the original form behavior with Site Evaluation specific logic
 */
export const useSiteEvalActionPlanForm = (
  initialData: any = {},
  onSubmit?: (result: any) => void
) => {
  // Form field states
  const [title, setTitle] = useState<string>(initialData.title || '');
  const [selectedType, setSelectedType] = useState<any>(initialData.type || null);
  const [selectedPriority, setSelectedPriority] = useState<any>(initialData.priority || null);
  const [selectedAssignee, setSelectedAssignee] = useState<any>(initialData.assignee ?? null);
  const [startDate, setStartDate] = useState<Date | null>(initialData.startDate || null);
  const [targetDate, setTargetDate] = useState<Date | null>(initialData.targetDate || null);
  const [actionPlan, setActionPlan] = useState<string>(initialData.actionPlan || '');
  const [notes, setNotes] = useState<string>(initialData.notes || '');
  const [frontlineInvolvement, setFrontlineInvolvement] = useState<boolean>(initialData.frontlineInvolvement || false);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(initialData.employee || null);

  // Sync form state when initialData changes (for reopening overlay with stored data)
  useEffect(() => {
    console.log('[useSiteEvalActionPlanForm] Syncing with initialData:', initialData);
    setTitle(initialData.title || '');
    setSelectedType(initialData.type || null);
    setSelectedPriority(initialData.priority || null);
    setSelectedAssignee(initialData.assignee ?? null);
    setStartDate(initialData.startDate || null);
    setTargetDate(initialData.targetDate || null);
    setActionPlan(initialData.actionPlan || '');
    setNotes(initialData.notes || '');
    setFrontlineInvolvement(initialData.frontlineInvolvement || false);
    setSelectedEmployee(initialData.employee || null);
  }, [
    initialData.title,
    initialData.type,
    initialData.priority,
    initialData.assignee,
    initialData.startDate,
    initialData.targetDate,
    initialData.actionPlan,
    initialData.notes,
    initialData.frontlineInvolvement,
    initialData.employee,
  ]);

  // Loading and error states
  const [loading, setLoading] = useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [dateValidationError, setDateValidationError] = useState<string>('');

  // API data states
  const [assigneeList, setAssigneeList] = useState<any[]>([]);
  const [assigneeLoading, setAssigneeLoading] = useState<boolean>(false);
  const [employeeList, setEmployeeList] = useState<any[]>([]);
  const [employeeLoading, setEmployeeLoading] = useState<boolean>(false);

  // Redux selectors
  const selectedLocationFromRedux = useSelector((state: RootState) => state.appData.selectedLocation);
  const authUserName = useSelector((state: RootState) => state.auth?.user?.name ?? state.auth?.user?.claims?.name ?? state.auth?.user?.username ?? '');
  const authUserId = useSelector((state: RootState) => state.auth?.user?.gpid ?? state.auth?.user?.id ?? null);
  const userInfoGPID = useSelector((state: RootState) => state.appData?.userInfo?.GPID ?? null);
  const authUserEmail = useSelector((state: RootState) => state.auth?.user?.username ?? '');
  const FieldUsers = useSelector((state: any) => state.appData.FieldUsers);
  const COEUsers = useSelector((state: any) => state.appData.COEUsers);

  const dispatch = useDispatch<AppDispatch>();

  // Fixed source for Site Evaluation
  const selectedSource = useMemo(() => ({ 
    id: 'site-evaluation', 
    displayText: 'Site Evaluation' 
  }), []);

  // Fixed owner based on current user
  const selectedOwner = useMemo(() => ({ 
    id: authUserId ?? 'owner', 
    displayText: formatAuthUserName(authUserName) || '--' 
  }), [authUserId, authUserName]);

  const isCreateDisabled = title.trim() === '' || actionPlan.trim() === '' || loading;

  // Source options with Site Evaluation only
  const sourceOptions = useMemo(() => [selectedSource], [selectedSource]);

  // Use existing options for other fields
  const typeOptions = useMemo(() => {
    // Add static site evaluation specific types
    const siteEvalTypes = [
      { id: 'kpi', displayText: 'KPI' },
      { id: 'kfa', displayText: 'KFA' },
      { id: 'process', displayText: 'PROCESS' }
    ];
    
    // Filter existing types relevant to Site Evaluation
    const existingTypes = TYPE_OPTIONS.filter(option => 
      ['Safety', 'Quality', 'Operational Excellence', 'General'].includes(option.displayText)
    );
    
    return [...siteEvalTypes, ...existingTypes];
  }, []);

  const priorityOptions = PRIORITY_OPTIONS;
  const ownerOption = [selectedOwner];

  // Format date for API
  const formatISODate = (d: Date | null) => {
    if (!d) return null;
    try {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    } catch (e) {
      return null;
    }
  };

  // Handle target date change with validation
  const handleTargetDateChange = (date: Date | null) => {
    setTargetDate(date);

    if (date && startDate) {
      const startDateObj = new Date(startDate);
      const targetDateObj = new Date(date);

      startDateObj.setHours(0, 0, 0, 0);
      targetDateObj.setHours(0, 0, 0, 0);

      if (targetDateObj < startDateObj) {
        setDateValidationError('Target Date must be equal or greater than Start Date');
      } else {
        setDateValidationError('');
      }
    } else {
      setDateValidationError('');
    }
  };

  // Load assignee list
  useEffect(() => {
    const loadAssignees = async () => {
      if (!FieldUsers && !COEUsers) return;

      setAssigneeLoading(true);
      try {
        const allUsers = [...(FieldUsers || []), ...(COEUsers || [])];
        const formattedAssignees = allUsers.map((user: any) =>
          transformUserToAssigneeOption(user)
        );
        setAssigneeList(formattedAssignees);
      } catch (error) {
        console.error('Error loading assignees:', error);
      } finally {
        setAssigneeLoading(false);
      }
    };

    loadAssignees();
  }, [FieldUsers, COEUsers]);

  // Load employee list
  useEffect(() => {
    const loadEmployees = async () => {
      if (!FieldUsers && !COEUsers) return;

      setEmployeeLoading(true);
      try {
        const allUsers = [...FieldUsers, ...COEUsers];
        const formatted = allUsers.map((user: any) => {
          // Format name from email if Name field is not available
          let displayName = user.Name;
          if (!displayName && user.UserEmail) {
            // Extract name from email (e.g., "John.Doe@pepsico.com" -> "John Doe")
            const emailPart = user.UserEmail.split('@')[0];
            const nameParts = emailPart.split(/[._-]/);
            displayName = nameParts
              .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
              .join(' ');
          }

          // Remove trailing 'Contractor' token from display names (case-insensitive)
          if (typeof displayName === 'string') {
            displayName = displayName.replace(/\s*Contractor\s*$/i, '').trim();
          }
          
          return {
            id: user.GPID || user.UserEmail,
            displayText: displayName || user.UserEmail || user.GPID,
            gpid: user.GPID,
            email: user.UserEmail,
            name: displayName || user.Name,
            role: user.Role || 'Employee',
          };
        });
        setEmployeeList(formatted);
      } catch (error) {
        console.error('Error formatting employee list:', error);
      } finally {
        setEmployeeLoading(false);
      }
    };

    loadEmployees();
  }, [FieldUsers, COEUsers]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    // Validate dates
    if (startDate && targetDate) {
      const startDateObj = new Date(startDate);
      const targetDateObj = new Date(targetDate);

      startDateObj.setHours(0, 0, 0, 0);
      targetDateObj.setHours(0, 0, 0, 0);

      if (targetDateObj < startDateObj) {
        setDateValidationError('Target Date must be equal or greater than Start Date');
        return null;
      }
    }

    setValidationErrors({});
    setSubmissionError(null);
    setDateValidationError('');

    // Comprehensive client-side validation for all required fields
    const requiredChecks: { key: string; present: boolean; label: string }[] = [
      { key: 'title', present: Boolean(title && title.trim()), label: 'Title' },
      { key: 'type', present: Boolean(selectedType), label: 'Type' },
      { key: 'priority', present: Boolean(selectedPriority), label: 'Priority' },
      { key: 'assignee', present: Boolean(selectedAssignee && (selectedAssignee.gpid || selectedAssignee.id || selectedAssignee.displayText)), label: 'Assignee' },
      { key: 'start_date', present: Boolean(startDate), label: 'Start Date' },
      { key: 'target_date', present: Boolean(targetDate), label: 'Target Date' },
      { key: 'action_plan', present: Boolean(actionPlan && actionPlan.trim()), label: 'Action Plan' },
    ];

    // If frontline involvement checkbox is checked, require employee
    if (frontlineInvolvement) {
      requiredChecks.push({ key: 'employee', present: Boolean(selectedEmployee && (selectedEmployee.gpid || selectedEmployee.id || selectedEmployee.displayText)), label: 'Employee Name' });
    }

    const missing = requiredChecks.filter((c) => !c.present);
    if (missing.length > 0) {
      // Build per-field validationErrors
      const fieldErrs: Record<string, string> = {};
      missing.forEach((m) => {
        fieldErrs[m.key] = `${m.label} is required`;
      });

      // Aggregate assignee related keys into single 'assignee' if backend may produce them
      if (fieldErrs['assignee_gpid'] || fieldErrs['assignee_email'] || fieldErrs['assignee_role'] || fieldErrs['assignee_role_id']) {
        fieldErrs['assignee'] = fieldErrs['assignee'] || 'Assignee is required';
        delete fieldErrs['assignee_gpid'];
        delete fieldErrs['assignee_email'];
        delete fieldErrs['assignee_role'];
        delete fieldErrs['assignee_role_id'];
      }

      // Ensure frontend uses 'employee' key instead of 'employee_gpid'
      if (fieldErrs['employee_gpid']) {
        fieldErrs['employee'] = fieldErrs['employee_gpid'];
        delete fieldErrs['employee_gpid'];
      }

      setValidationErrors((prev) => ({ ...prev, ...fieldErrs }));

      const friendlyNames = missing.map((m) => m.label);
      const banner = `Required fields are missing: ${friendlyNames.join(', ')}`;

      setSubmissionError({ title: 'Validation Error', message: banner, details: null });

      // Note: Toast notification will be handled by the overlay component
      // which has access to the proper toast context for the Site Evaluation screen

      return null;
    }

    setLoading(true);

    try {
      // Prepare the result with raw form objects for proper restoration
      // This object contains the original form field values (objects, not strings)
      const formResult = {
        title: title.trim(),
        source: selectedSource,
        type: selectedType,  // Keep as object for restoration
        selectedType: selectedType, // Also store with alternate name
        priority: selectedPriority, // Keep as object
        selectedPriority: selectedPriority, // Also store with alternate name
        assignee: selectedAssignee, // Keep as object
        selectedAssignee: selectedAssignee, // Also store with alternate name
        startDate: formatISODate(startDate), // YYYY-MM-DD string, no timezone involvement
        targetDate: formatISODate(targetDate), // YYYY-MM-DD string, no timezone involvement
        actionPlan: actionPlan.trim(),
        notes: notes.trim(),
        frontlineInvolvement: frontlineInvolvement,
        employee: selectedEmployee, // Keep as object
        selectedEmployee: selectedEmployee, // Also store with alternate name
        // Additional fields for API payload
        assignee_gpid: selectedAssignee?.gpid || selectedAssignee?.id || authUserId,
        assignee_email: selectedAssignee?.email || selectedAssignee?.id || authUserEmail,
        location: selectedLocationFromRedux?.location || '',
        action_plan_owner_gpid: userInfoGPID || authUserId,
        action_plan_owner_email: authUserEmail,
        created_by_email: authUserEmail,
        _source_context: 'site_evaluation',
        _created_from: 'site_evaluation_overlay'
      };

      console.log('[useSiteEvalActionPlanForm] Form result with objects:', formResult);

      // Return the form result - let the overlay handle storage
      setLoading(false);
      return formResult;
    } catch (error: any) {
      console.error('Error creating site evaluation action plan:', error);

      let errorMessage = 'Failed to create action plan';
      let errorTitle = 'Error';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        setValidationErrors(error.response.data.errors);
      }

      // Parse missingFields from backend response
      if (error.response?.data?.details?.missingFields) {
        const missingFields = error.response.data.details.missingFields;
        
        // Map backend field names to frontend keys
        const fieldLabelMap: Record<string, string> = {
          title: 'Title',
          source: 'Source',
          type: 'Type',
          priority: 'Priority',
          assignee: 'Assignee',
          assignee_gpid: 'Assignee',
          assignee_email: 'Assignee',
          assignee_role: 'Assignee',
          assignee_role_id: 'Assignee',
          start_date: 'Start Date',
          target_date: 'Target Date',
          action_plan: 'Action Plan',
          action_plan_description: 'Action Plan',
          employee: 'Employee Name',
          employee_gpid: 'Employee Name',
        };

        const fieldErrors: Record<string, string> = {};
        missingFields.forEach((field: string) => {
          const label = fieldLabelMap[field] || field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          fieldErrors[field] = `${label} is required`;
        });

        // Aggregate assignee-related errors into a single 'assignee' key
        const assigneeRelatedKeys = Object.keys(fieldErrors).filter(k => k.startsWith('assignee'));
        if (assigneeRelatedKeys.length > 0) {
          const combined = assigneeRelatedKeys
            .map(k => fieldErrors[k])
            .filter(Boolean)
            .join('; ');
          fieldErrors['assignee'] = combined || 'Assignee is required';
          assigneeRelatedKeys.forEach(k => delete fieldErrors[k]);
        }

        // Aggregate employee-related backend keys into single 'employee' key
        const employeeRelatedKeys = Object.keys(fieldErrors).filter(k => k.startsWith('employee'));
        if (employeeRelatedKeys.length > 0) {
          const combinedEmp = employeeRelatedKeys.map(k => fieldErrors[k]).filter(Boolean).join('; ');
          fieldErrors['employee'] = combinedEmp || 'Employee Name is required';
          employeeRelatedKeys.forEach(k => delete fieldErrors[k]);
        }

        setValidationErrors(fieldErrors);

        const friendlyNames = Object.values(fieldLabelMap)
          .filter((label, idx) => missingFields.includes(Object.keys(fieldLabelMap)[idx]));
        errorMessage = `Required fields are missing: ${friendlyNames.join(', ')}`;
        errorTitle = 'Validation Error';
      }

      setSubmissionError({
        title: errorTitle,
        message: errorMessage,
      });

      // Note: Toast notification will be handled by the overlay component
      // which has access to the proper toast context for the Site Evaluation screen

      return null;
    } finally {
      setLoading(false);
    }
  }, [
    title,
    actionPlan,
    frontlineInvolvement,
    selectedEmployee,
    startDate,
    targetDate,
    selectedSource,
    selectedType,
    selectedPriority,
    selectedAssignee,
    notes,
    authUserId,
    authUserEmail,
    userInfoGPID,
    selectedLocationFromRedux,
    dispatch,
  ]);

  // Clear per-field validation errors when user updates the corresponding fields
  useEffect(() => {
    if (title && title.trim()) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.title; return next; });
    }
  }, [title]);

  useEffect(() => {
    if (selectedType) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.type; return next; });
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedPriority) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.priority; return next; });
    }
  }, [selectedPriority]);

  useEffect(() => {
    if (selectedAssignee) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next.assignee;
        delete next.assignee_gpid;
        delete next.assignee_email;
        delete next.assignee_role;
        delete next.assignee_role_id;
        return next;
      });
    }
  }, [selectedAssignee]);

  useEffect(() => {
    if (startDate) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.start_date; return next; });
    }
  }, [startDate]);

  useEffect(() => {
    if (targetDate) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.target_date; return next; });
    }
  }, [targetDate]);

  useEffect(() => {
    if (actionPlan && actionPlan.trim()) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.action_plan; delete next.action_plan_description; return next; });
    }
  }, [actionPlan]);

  useEffect(() => {
    if (selectedEmployee) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next.employee; delete next.employee_gpid; return next; });
    }
  }, [selectedEmployee]);

  return {
    // Form state
    title,
    setTitle,
    selectedSource, // Read-only for Site Evaluation
    selectedType,
    setSelectedType,
    selectedPriority,
    setSelectedPriority,
    selectedOwner, // Read-only
    selectedAssignee,
    setSelectedAssignee,
    startDate,
    setStartDate,
    targetDate,
    setTargetDate,
    handleTargetDateChange,
    actionPlan,
    setActionPlan,
    notes,
    setNotes,
    frontlineInvolvement,
    setFrontlineInvolvement,
    selectedEmployee,
    setSelectedEmployee,

    // Options
    sourceOptions, // Only Site Evaluation
    typeOptions,
    priorityOptions,
    ownerOption, // Read-only
    assigneeList,
    employeeList,

    // State flags
    loading,
    assigneeLoading,
    employeeLoading,
    isCreateDisabled,
    dateValidationError,
    submissionError,
    validationErrors,
    setSubmissionError,
    setValidationErrors,

    // Actions
    handleSubmit,
    formatISODate,
  };
};